import React, { useMemo } from 'react';
import { Button } from '@vfs-digital-channels/ns-react-components';
import { ShowIf } from '../../../../components';
import { PaymentStatus } from 'src/features/common/types/application/payment';
import {
  ApplicationActionType,
  DataStatus,
  HelpButton,
  isOnlyDeviceSelected,
  PageLayout,
  ScrollTop,
  SolutionType,
  useWizardEffect,
} from '../../../common';
import { PaymentTimeout } from 'src/features/common/components/payment-status/payment-timeout';
import { MAX_NUMBER_OF_TRIES, publicSiteUrl } from 'src/common';
import { useApplication } from 'src/hooks/useApplication';
import { DEVICE_NAMES } from 'src/features/order/tech-debt/product-details-extra';
import { FailurePage, SuccessPage } from 'src/features/order/components';
import { HelpButtonAction, PageName } from 'src/features/order/services/order-tealium-services';
import {
  calculateCartTotal,
  filterCartItemsByPriceType,
  isItemsInCart,
  isSingleItemInCart,
  PriceType,
} from '@vfs-digital-channels/ns-utils';
import { getProductNamesInCart } from '../../helpers/tealium-service-helpers';
import { getApplicationAPIFromApplication } from 'src/features/common/helpers/dto-mappers/salesforce/get-application-api-from-application';
import { Environment } from 'src/Environment';
import { useDeepCompareEffect } from 'src/hooks/useDeepCompareEffect';
import { onboardingBrokerRoutePos } from 'src/routing/routes';
import { orderPaymentRoute } from 'src/features/order/helpers/routes';
import { cn } from 'src/utils/cn';

export function OrderPostSubmitResult() {
  const {
    application,
    dispatch,
    catalogue,
    salesforceService,
    dpoTealiumService,
    orderTealiumService,
  } = useApplication();
  const onceOffDevices = filterCartItemsByPriceType(application.cart || [], PriceType.ONCE_OFF);

  const totalAmount = calculateCartTotal(onceOffDevices);
  const isPaymentRequired = totalAmount > 0;
  const paymentFailurePageName = PageName.PAYMENT_FAILURE;
  const paymentSuccessPageName = PageName.ORDER_COMPLETE;
  const isPaymentSuccessful =
    application?.payment?.paymentStatus === PaymentStatus.PAYMENT_SUCCESSFUL ||
    (application.solutionType === SolutionType.thirdPartyOnboarding &&
      !application.payment?.onlinePaymentRequired &&
      application?.payment?.paymentStatus !== PaymentStatus.PAYMENT_FAILED);

  const eCommerceOnlySelected = useMemo(
    () =>
      !Environment.current.disabledFeatures.dpo
        ? isSingleItemInCart(application.cart ?? [], DEVICE_NAMES.VODAPAY_ECOM_GATEWAY)
        : isOnlyDeviceSelected(catalogue, DEVICE_NAMES.VODAPAY_ECOM_GATEWAY),
    [catalogue.products, application.cart],
  );

  const kwikaOrMaxSelected = useMemo(
    () =>
      isItemsInCart(application.cart || [], [DEVICE_NAMES.VODAPAY_KWIKA, DEVICE_NAMES.VODAPAY_MAX]),
    [application.cart],
  );
  const maxNumberOfTries =
    (application.remotePayment?.numberOfTries || application.gatewayPayment?.numberOfTries || 0) >=
    (application.payment?.maxNumberOfTries ?? MAX_NUMBER_OF_TRIES);

  const productNames = getProductNamesInCart(application.cart || []);

  const errorMessage = useMemo(() => {
    if (application.gatewayPayment?.responseMessage || application.remotePayment?.responseMessage) {
      return maxNumberOfTries
        ? 'Please contact your bank for more information'
        : 'Please try again or contact your bank for more information';
    }
  }, [application]);

  const revampEnabled = useMemo((): boolean => !Environment.current.disabledFeatures.revamp, []);

  const saveApp = async () => {
    await salesforceService.saveApplication(
      getApplicationAPIFromApplication(application, catalogue),
      true,
    );
  };

  useWizardEffect(() => {
    return {
      nextButtonVisible: false,
      backButtonVisible: false,
      footerVisible: false,
      nextButtonEnabled: true,
      onNextButtonClicked: () => {
        if (!isPaymentRequired || isPaymentSuccessful) {
          dispatch({ type: ApplicationActionType.NAVIGATE_TO_NEXT_STEP });
        }
      },
      navigateBackEnabled: isPaymentRequired && !isPaymentSuccessful,
      hideProgressBar: application.solutionType === SolutionType.buyInStoreFicaOnline,
    };
  }, [application, isPaymentSuccessful, isPaymentRequired]);

  useDeepCompareEffect(() => {
    if (application.gatewayPayment?.initiated && catalogue.statusAll === DataStatus.LOADED) {
      // Send response code and message to SF
      saveApp();
    }
  }, [application, catalogue.statusAll]);

  const onAnalyticsSuccessfulPageStart = () => {
    dpoTealiumService?.onSuccessPageView(application.cart || [], application.referenceNumber!);
  };

  const onPaymentFailureRetry = async (isTimeout?: boolean) => {
    !isTimeout && orderTealiumService?.onPaymentFailureAction();
    if (application.gatewayPayment?.initiated) {
      dispatch({
        type: ApplicationActionType.MULTIPLE_ACTIONS,
        actions: [
          { type: ApplicationActionType.RESET_GATEWAY_PAYMENT_STATUS },
          { type: ApplicationActionType.NAVIGATE_TO_PREVIOUS_STEP },
        ],
      });
      return;
    }
    dispatch({
      type: ApplicationActionType.MULTIPLE_ACTIONS,
      actions: [
        { type: ApplicationActionType.RESET_REMOTE_PAYMENT_STATUS },
        { type: ApplicationActionType.NAVIGATE_TO_PREVIOUS_STEP },
      ],
    });
    if (application.isContinueWithOTP)
      window.location.href = `${Environment.current.uiHost}${onboardingBrokerRoutePos}/${orderPaymentRoute}`;
  };

  if (application.payment?.paymentStatus === PaymentStatus.TIMEOUT || maxNumberOfTries) {
    return (
      <PaymentTimeout
        maxNumberOfTries={maxNumberOfTries}
        subStatus={errorMessage}
        onClick={() => {
          if (maxNumberOfTries) {
            window.location.href = publicSiteUrl;
            return;
          }
          dpoTealiumService?.onPaymentFailureRetry(productNames);

          onPaymentFailureRetry(true);
        }}
      />
    );
  }

  return (
    <section className="OrderPostSubmitResult">
      <PageLayout>
        <ScrollTop />
        <ShowIf condition={!isPaymentRequired || isPaymentSuccessful}>
          <SuccessPage
            onTealiumPageStart={() => onAnalyticsSuccessfulPageStart()}
            eCommerceOnlySelected={eCommerceOnlySelected}
            contactName={application.contact?.name}
            paymentGateway={application.gatewayPayment?.initiated}
          />
          <div className="flex justify-center w-full mt-10">
            <Button
              type="button"
              size="md"
              className={cn('w-65', {
                ['!bg-blue-700']: revampEnabled,
                ['!bg-red-600']: !revampEnabled,
              })}
              onClick={() => {
                if (!isPaymentRequired || isPaymentSuccessful) {
                  dispatch({ type: ApplicationActionType.NAVIGATE_TO_NEXT_STEP });
                }
              }}
            >
              {kwikaOrMaxSelected ? 'Create my profile now' : 'Setup my account'}
            </Button>
          </div>
          <HelpButton
            onOpen={() =>
              orderTealiumService?.onHelpButtonActions(
                paymentSuccessPageName,
                HelpButtonAction.CLICK,
              )
            }
            onEmailClicked={() =>
              orderTealiumService?.onHelpButtonActions(
                paymentSuccessPageName,
                HelpButtonAction.EMAIL,
              )
            }
            onPhoneNumberClicked={() =>
              orderTealiumService?.onHelpButtonActions(
                paymentSuccessPageName,
                HelpButtonAction.CALL,
              )
            }
          />
        </ShowIf>

        <ShowIf condition={isPaymentRequired && !isPaymentSuccessful}>
          <FailurePage
            onFailurePageShow={() => {
              dpoTealiumService?.paymentFailureLanding(
                application.remotePayment?.responseMessage || '',
                application.remotePayment?.responseCode || '',
              );
            }}
            onFailurePageDispatch={() => {
              dpoTealiumService?.onPaymentFailureRetry(productNames);

              onPaymentFailureRetry();
            }}
            errorMessage={errorMessage}
          />
          <HelpButton
            onOpen={() =>
              orderTealiumService?.onHelpButtonActions(
                paymentFailurePageName,
                HelpButtonAction.CLICK,
              )
            }
            onEmailClicked={() =>
              orderTealiumService?.onHelpButtonActions(
                paymentFailurePageName,
                HelpButtonAction.EMAIL,
              )
            }
            onPhoneNumberClicked={() =>
              orderTealiumService?.onHelpButtonActions(
                paymentFailurePageName,
                HelpButtonAction.CALL,
              )
            }
          />
        </ShowIf>
      </PageLayout>
    </section>
  );
}
